import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  Output,
  signal,
  Signal,
  computed,
  effect,
  inject,
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { TranslateModule } from '@ngx-translate/core';
import {
  GalaxyAbstractFilterInput,
  GalaxyFilterValueInterface,
  InnerGalaxyFilterChipInjectionToken,
} from '../interface';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { Subscription, shareReplay } from 'rxjs';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { distinctUntilChanged, filter, switchMap } from 'rxjs/operators';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { MatCheckboxModule } from '@angular/material/checkbox';

@Component({
  selector: 'glxy-checkbox-filter-input',
  template: `
    <glxy-form-field class="filter-string-input" [bottomSpacing]="'none'" [formGroup]="formGroup">
      <mat-checkbox *ngFor="let control of controls()" [formControlName]="control.option">
        {{ control.option }}
      </mat-checkbox>
    </glxy-form-field>
  `,
  providers: [
    { provide: GalaxyAbstractFilterInput, useExisting: forwardRef(() => GalaxyCheckboxFilterInputComponent) },
  ],
  imports: [
    CommonModule,
    TranslateModule,
    ReactiveFormsModule,
    MatInputModule,
    GalaxyFormFieldModule,
    MatAutocompleteModule,
    MatCheckboxModule,
  ],

  standalone: true,
})
export class GalaxyCheckboxFilterInputComponent implements GalaxyAbstractFilterInput, OnDestroy {
  @Input() set fieldId(value: string) {
    this._fieldId.set(value);
  }
  @Input() set disabled(value: boolean) {
    if (value) {
      this.formGroup.disable();
    }
  }

  @Output() valueChanges = new EventEmitter<GalaxyFilterValueInterface[]>();
  formGroup: FormGroup = new FormGroup({});

  _fieldId = signal('');
  availableOptions = signal<string[]>([]);
  _initialValue = signal<GalaxyFilterValueInterface[]>([]);
  formValueChanges = toSignal(this.formGroup.valueChanges);
  controls: Signal<Array<{ formControl: FormControl; option: string }>> = computed(() => {
    let controls: Array<{ formControl: FormControl; option: string }> = [];
    Object.keys(this.formValueChanges() ?? {}).forEach((option) => {
      controls = [...controls, { option, formControl: this.formGroup.get(option) as FormControl }];
    });
    return controls;
  });

  @Input() set initialValue(value: GalaxyFilterValueInterface[]) {
    this._initialValue.set(value);
  }

  subscriptions: Subscription[] = [];

  private readonly filtersDependencies = inject(InnerGalaxyFilterChipInjectionToken);

  constructor(private readonly formBuilder: FormBuilder) {
    effect(
      () => {
        const currentValues = this._initialValue().map((value) => value.string);
        const options = this.availableOptions();
        Object.keys(this.formGroup.controls).forEach((option) => {
          if (!options.includes(option)) {
            this.formGroup.removeControl(option);
          }
        });
        options.forEach((option) => {
          let control = this.formGroup.get(option);
          if (!control) {
            control = this.formBuilder.control(currentValues.includes(option));
            this.formGroup.addControl(option, control);
          }
          if (control.pristine) {
            control.setValue(currentValues.includes(option));
          }
        });
      },
      { allowSignalWrites: true },
    );

    const fieldId$ = toObservable(this._fieldId);

    this.subscriptions.push(
      this.formGroup.valueChanges
        .pipe(distinctUntilChanged((a, b) => Object.keys(a).sort().join('') === Object.keys(b).sort().join()))
        .subscribe((value) => {
          const values: GalaxyFilterValueInterface[] = [];
          Object.entries(value).forEach(([option, checked]) => {
            if (checked) {
              values.push({ string: option });
            }
          });
          this.valueChanges.emit(values);
        }),
      fieldId$
        .pipe(
          filter((fieldId) => fieldId.length > 0),
          switchMap((fieldId) => this.filtersDependencies.getFieldOptions(fieldId, '')),
          shareReplay(1),
        )
        .subscribe((options) => {
          this.availableOptions.set(options);
        }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  clearValue() {
    this.formGroup.reset();
    Object.values(this.formGroup.controls).forEach((control) => control.setValue(false));
  }
}
